import { isEqual } from 'lodash-es';

import { insertInArrayByIndex } from '@editor2/services/helpersService';
import { isDoubleElement } from '@editor2/services/elementService';
import { type CourseState } from '@editor2/types/courseState';
import { type DoubleElements } from '@editor2/types/pageElements';
import { ChangesStatus } from '@editor2/types/changesStatus';

import { addStatus, deleteProperty, isEqualIdsArrays, removeStatus } from './helpers';

export const indentElements = (state: CourseState, elements: string[]) => {
	let indent = false;
	elements.forEach((elementId: string) => {
		const element = state.elements.entities[elementId];

		state.elements.entities[elementId] = {
			...element,
			indent: isDoubleElement(element.type)
				? indent && (element as DoubleElements).direction === 'in'
				: indent
		};

		if (isDoubleElement(element.type)) indent = (element as DoubleElements).direction === 'in';
	});
};

export const addElementHelper = (state: CourseState, payload) => {
	const { pageId, elementEntity, index } = payload;
	const pageElements = state.pages.entities[pageId].elements;
	const ind = index !== undefined ? index : pageElements.length;

	const elementsIds = insertInArrayByIndex(pageElements, elementEntity.family_id, ind);

	state.elements.entities[elementEntity.family_id] = { ...elementEntity, initPageId: pageId };
	state.elements.ids.push(elementEntity.family_id);

	state.pages.entities[pageId].elements = elementsIds;

	indentElements(state, elementsIds);

	state.changes.elements[elementEntity.family_id] = [ChangesStatus.New];
	state.changes.pages[pageId] = addStatus(
		state.changes.pages[pageId],
		ChangesStatus.ChildrenReorder
	);

	return state;
};

export const moveElementHelper = (state: CourseState, payload) => {
	const { sourceDroppableId, destinationDroppableId, destinationIndex, elementId } = payload;

	const initSourcePageElements =
		state.initialCourse.pages.entities[sourceDroppableId]?.elements || [];
	const sourcePageElements = state.pages.entities[sourceDroppableId].elements.filter(
		(id) => id !== elementId
	);

	const initDestinationPageElements =
		state.initialCourse.pages.entities[destinationDroppableId]?.elements || [];
	const destinationElements =
		sourceDroppableId === destinationDroppableId
			? sourcePageElements
			: state.pages.entities[destinationDroppableId].elements;
	const destinationPageElements = insertInArrayByIndex(
		destinationElements,
		elementId,
		destinationIndex
	);

	indentElements(state, destinationPageElements);

	state.pages.entities[sourceDroppableId].elements = sourcePageElements;
	state.pages.entities[destinationDroppableId].elements = destinationPageElements;

	const isSourceEqual = isEqualIdsArrays(initSourcePageElements, sourcePageElements);
	const isDestinationEqual = isEqualIdsArrays(initDestinationPageElements, destinationPageElements);

	state.changes.pages[sourceDroppableId] = isSourceEqual
		? removeStatus(state.changes.pages[sourceDroppableId], ChangesStatus.ChildrenReorder)
		: addStatus(state.changes.pages[sourceDroppableId], ChangesStatus.ChildrenReorder);

	state.changes.pages[destinationDroppableId] = isDestinationEqual
		? removeStatus(state.changes.pages[destinationDroppableId], ChangesStatus.ChildrenReorder)
		: addStatus(state.changes.pages[destinationDroppableId], ChangesStatus.ChildrenReorder);

	return state;
};

export const deleteElementHelper = (state: CourseState, payload) => {
	const { pageId, elementId } = payload;

	const initialElementsIds = state.initialCourse.pages.entities[pageId]?.elements || [];
	const elementsIds = state.pages.entities[pageId].elements.filter((id) => id !== elementId);

	indentElements(state, elementsIds);

	state.elements.entities = deleteProperty(state.elements.entities, elementId);
	state.pages.entities[pageId].elements = elementsIds;

	const isNew = state.changes.elements[elementId]?.includes(ChangesStatus.New);
	state.changes.elements[elementId] = isNew ? [] : [ChangesStatus.Deleted];

	state.changes.pages[pageId] = isEqualIdsArrays(initialElementsIds, elementsIds)
		? removeStatus(state.changes.pages[pageId], ChangesStatus.ChildrenReorder)
		: addStatus(state.changes.pages[pageId], ChangesStatus.ChildrenReorder);

	return state;
};

export const updateElementHelper = (state: CourseState, payload) => {
	const { elementId, elementMetadata, ...element } = payload;

	const initialElements = state.initialCourse.elements.entities[elementId];
	const updatedElement = {
		...state.elements.entities[elementId],
		...(elementMetadata !== undefined && { metadata: elementMetadata }),
		...element
	};

	state.elements.entities[elementId] = updatedElement;

	state.changes.elements[elementId] = isEqual(initialElements, updatedElement)
		? removeStatus(state.changes.elements[elementId], ChangesStatus.Modified)
		: addStatus(state.changes.elements[elementId], ChangesStatus.Modified);

	return state;
};
